import { useContext, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Typography } from "@mui/material";
import Tabs from "./../UI/Tabs/Tabs";
import Rankings from "./Rankings";
import SentimentTable from "./SentimentTable";
import SentimentDriversTable from "./SentimentDrivers";
import RBIFormulaBreakdown from "./RBIFormulaBreakdown";
import BaseResponsesModal from "../../Component/BaseResponseComponent/BaseResponsesModal";
import useBaseResponsesModal from "../../Component/BaseResponseComponent/useBaseResponsesModal";
import { DiscoveryContext } from "../../luminaire/DiscoveryV2/discovery-context";
import { AuthContext } from "../../context/auth-context";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import { transformDriverData } from "../../utils/helpers";
import {
  getBrandPillarsDetail,
  getSentimentScoreByFactor,
  calcDirectIndexScoreForEntity,
} from "../../utils/brandIndexCalc";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function BrandIndexBreakupsNewV3({
  data,
  brandProduct,
  selectedInput,
  fromDiscoveryV2 = false,
  compIndexScoreMetaData = [],
}) {
  const discoveryCtx = useContext(DiscoveryContext);
  const { user } = useContext(AuthContext);
  const isCompanyUser = user?.user_role?.role?.name === "Company user";

  const isProduct = discoveryCtx.type === "product";
  const pillarsLabel = isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar;

  const {
    infoModalOpen, infoModalTitle, infoModalContent, closeInfoModal,
    productMappingAttribute,
  } = useBaseResponsesModal(data, "visibility");

  const rankRow = data?.rank?.length === 1
    ? data.rank[0]
    : data?.rank?.find((r) => r.name === brandProduct) ?? data?.rank?.[0];

  const sentimentScoreByFactor = getSentimentScoreByFactor(brandProduct, data?.favorabilities);
  const brandPillarsDetail = isProduct ? [] : getBrandPillarsDetail(brandProduct, discoveryCtx.core_attributes);

  const direct = calcDirectIndexScoreForEntity({
    rankScoreByFactor: rankRow?.rankScoreByFactor,
    sentimentScoreByFactor,
    brandPillarsDetail,
  });

  const [selectedRankVariant, setSelectedRankVariant] = useState("");
  useEffect(() => {
    if (direct.rankVariants.length && !direct.rankVariants.includes(selectedRankVariant)) {
      setSelectedRankVariant(direct.rankVariants[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direct.rankVariants.join("|")]);

  function buildFactorScopedSentimentData(factor) {
    return {
      ...data,
      sentimentMeta: (data?.sentimentMeta || []).filter((g) => g?.[0]?.factor === factor),
      favorabilities: (data?.favorabilities || []).map((fav) => ({
        ...fav,
        sentimentScoreByFactor: fav.sentimentScoreByFactor
          ? { [factor]: fav.sentimentScoreByFactor[factor] }
          : fav.sentimentScoreByFactor,
      })),
    };
  }

  const sentimentDrivers = data?.sentiementKeyDrivers;
  const isFactorGroupedDrivers =
    (sentimentDrivers?.positive && !Array.isArray(sentimentDrivers.positive) && typeof sentimentDrivers.positive === "object") ||
    (sentimentDrivers?.negative && !Array.isArray(sentimentDrivers.negative) && typeof sentimentDrivers.negative === "object");
  const positiveDrivers = isFactorGroupedDrivers && direct.sentimentFactor
    ? (sentimentDrivers?.positive?.[direct.sentimentFactor] ?? [])
    : [];
  const negativeDrivers = isFactorGroupedDrivers && direct.sentimentFactor
    ? (sentimentDrivers?.negative?.[direct.sentimentFactor] ?? [])
    : [];

  const activeRankVariant = direct.rankVariants.length
    ? (selectedRankVariant || direct.rankVariants[0])
    : null;
  const activeRankIndices = activeRankVariant ? (productMappingAttribute?.[activeRankVariant] ?? []) : [];

  return (
    <>
      <h3>Direct Index Score</h3>
      <Typography variant="button" color="textSecondary">
        {data?.categoryName}
      </Typography>
      <hr />

      <div className="mx-4">
        <TableContainer>
          <h5>{direct.factorLabel}</h5>
          <hr />

          <h6>A. {discoveryCtx?.mapMetricTitle?.(ENTITY_LABELS.rank) ?? ENTITY_LABELS.rank}</h6>
          <div style={{ marginBottom: 8, fontSize: 14 }}>
            Score (averaged across variants): <strong>{direct.visRaw != null ? Math.round(direct.visRaw) : "—"}</strong>
          </div>

          {direct.rankVariants.length > 1 && (
            <Tabs
              options={direct.rankVariants.map((f) => ({ name: f, value: f }))}
              selected={activeRankVariant}
              onChange={setSelectedRankVariant}
            />
          )}

          {activeRankVariant ? (
            <>
              <h6 style={{ fontSize: 13, marginTop: 12 }}>A.1. {activeRankVariant} — {discoveryCtx?.mapMetricTitle?.(`${ENTITY_LABELS.rank} Data`) ?? `${ENTITY_LABELS.rank} Data`}</h6>
              {activeRankIndices.length > 0 && data?.productRankingsArray ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  <Rankings
                    rankings={activeRankIndices.map((i) => data.productRankingsArray[i])}
                    pIndices={activeRankIndices}
                    sectionIndices={activeRankIndices.map((_, i) => i)}
                    rankMatchesIndices={data?.rankMatchesIndices}
                    name={data?.favorabilities?.length > 0 ? data.favorabilities[0]?.name : ""}
                  />
                </div>
              ) : (
                <Typography variant="body2" color="textSecondary">No visibility datasets for this variant.</Typography>
              )}

              <h6 style={{ fontSize: 13, marginTop: 12 }}>A.2. {activeRankVariant} — {discoveryCtx?.mapMetricTitle?.(`${ENTITY_LABELS.rank} Score`) ?? `${ENTITY_LABELS.rank} Score`}</h6>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 300 }}>
                  <TableHead>
                    <StyledTableRow>
                      <StyledTableCell>Name</StyledTableCell>
                      <StyledTableCell align="right">Score</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.rank || []).map((row) => (
                      <StyledTableRow key={row.name}>
                        <StyledTableCell component="th" scope="row">{row.name}</StyledTableCell>
                        <StyledTableCell align="right">{row.rankScoreByFactor?.[activeRankVariant]?.score ?? "—"}</StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">No visibility data for this factor.</Typography>
          )}

          <hr />
          <h6>B. {discoveryCtx?.mapMetricTitle?.("Sentiment") ?? "Sentiment"}</h6>
          <hr />
          {direct.sentimentFactor ? (
            <SentimentTable
              data={buildFactorScopedSentimentData(direct.sentimentFactor)}
              selectedInput={selectedInput}
              fromDashboardBrandScore={fromDiscoveryV2}
              compIndexScoreMetaData={compIndexScoreMetaData}
              brandProduct={brandProduct}
              showInfoIcon={!isCompanyUser}
              embedded
              scoresHeading={`B.1. ${discoveryCtx?.mapMetricTitle?.("Average Sentiment Score") ?? "Average Sentiment Score"}`}
              idSuffix={`_direct_${direct.sentimentFactor}`}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">No sentiment data for this factor.</Typography>
          )}

          {(positiveDrivers.length > 0 || negativeDrivers.length > 0) && (
            <>
              <hr />
              <h6>B.2. {discoveryCtx?.mapMetricTitle?.("Sentiment Drivers") ?? "Sentiment Drivers"}</h6>
              <hr />
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {positiveDrivers.length > 0 && (
                  <div style={{ flex: 1, minWidth: "40%" }}>
                    <SentimentDriversTable
                      data={transformDriverData(positiveDrivers, "Positive")}
                      title={discoveryCtx?.mapMetricTitle?.("Positive Sentiment Drivers") ?? "Positive Sentiment Drivers"}
                      id="direct_positive_sentimentdrivers_id"
                      driverType="positive"
                      sentimentData={data}
                      showInfoIcon={!isCompanyUser}
                    />
                  </div>
                )}
                {negativeDrivers.length > 0 && (
                  <div style={{ flex: 1, minWidth: "40%" }}>
                    <SentimentDriversTable
                      data={transformDriverData(negativeDrivers, "Negative")}
                      title={discoveryCtx?.mapMetricTitle?.("Negative Sentiment Drivers") ?? "Negative Sentiment Drivers"}
                      id="direct_negative_sentimentdrivers_id"
                      driverType="negative"
                      sentimentData={data}
                      showInfoIcon={!isCompanyUser}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          <hr />
          <h6>C. {pillarsLabel}</h6>
          <div style={{ fontSize: 14 }}>
            Score: <strong>{direct.pillarRaw != null ? Number(direct.pillarRaw).toFixed(2) : "—"}</strong>
          </div>

          <hr />
          <h5>Direct Index Score Formula</h5>
          <hr />
          <RBIFormulaBreakdown
            rank={direct.visRaw}
            favorability={direct.sentRaw}
            brandPillarsAvg={direct.pillarRaw}
            pillarsLabel={pillarsLabel}
            isDirect
          />
        </TableContainer>
      </div>

      <BaseResponsesModal
        open={infoModalOpen}
        title={infoModalTitle}
        content={infoModalContent}
        onClose={closeInfoModal}
      />
    </>
  );
}
